import { FC, useState, useEffect } from 'react';
import { Autocomplete, TextField, CircularProgress, Box, Tooltip, IconButton } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';
import { StudioPromptQuery } from '../../../services/query/StudioPromptQuery';
import { StudioPrompt, PromptType } from '../../../types/StudioPrompt';
import { PromptEnhanceModal } from './PromptEnhanceModal';
import { StudioPromptOptionItem } from './StudioPromptOptionItem';

interface StudioPromptSelectorProps {
    promptText: string;
    onPromptTextChange: (text: string) => void;
    promptType?: PromptType;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    label?: string;
    placeholder?: string;
    disableSelection?: boolean;
}

export const StudioPromptSelector: FC<StudioPromptSelectorProps> = ({
    promptText,
    onPromptTextChange,
    promptType,
    required = false,
    error = false,
    helperText,
    label,
    placeholder,
    disableSelection = false,
}) => {
    const { t } = useTranslation();
    const inputMode = disableSelection ? 'manual' : 'selection';
    const [open, setOpen] = useState(false);
    const [options, setOptions] = useState<StudioPrompt[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPrompt, setSelectedPrompt] = useState<StudioPrompt | null>(null);

    useEffect(() => {
        if (open) {
            loadPrompts();
        }
    }, [open, searchTerm, promptType]);

    const loadPrompts = async () => {
        setLoading(true);
        try {
            const response = await StudioPromptQuery.GetStudioPrompts({
                name: searchTerm || undefined,
                promptType: promptType,
                isActive: true,
                pageNumber: 1,
                pageSize: 50,
            });

            if (response?.model?.items) {
                setOptions(response.model.items);
            } else if (response?.data?.model?.items) {
                // Fallback for different response structure
                setOptions(response.data.model.items);
            }
        } catch (error) {
            console.error('Error loading prompts:', error);
            setOptions([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePromptSelect = (prompt: StudioPrompt | null) => {
        setSelectedPrompt(prompt);
        if (prompt) {
            onPromptTextChange(prompt.promptText);
        }
    };

    const handleTextChange = (text: string) => {
        onPromptTextChange(text);
    };

    const [enhanceModalOpen, setEnhanceModalOpen] = useState(false);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Prompt İsmi Seçimi - Autocomplete (Sadece Seçim modunda) */}
            {inputMode === 'selection' && !disableSelection && (
                <Autocomplete
                    open={open}
                    onOpen={() => setOpen(true)}
                    onClose={() => setOpen(false)}
                    options={options}
                    loading={loading}
                    value={selectedPrompt}
                    onChange={(_, newValue) => {
                        handlePromptSelect(newValue);
                    }}
                    onInputChange={(_, newInputValue) => {
                        setSearchTerm(newInputValue);
                    }}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    renderOption={(props, option) => (
                        <Box
                            component="li"
                            {...props}
                            key={option.id}
                            sx={{ alignItems: 'flex-start !important', width: '100%' }}
                        >
                            <StudioPromptOptionItem option={option} hidePromptTypeChip={Boolean(promptType)} />
                        </Box>
                    )}
                    slotProps={{
                        paper: {
                            sx: { width: '100%', maxWidth: '100%' },
                        },
                        listbox: {
                            sx: { width: '100%', maxWidth: '100%' },
                        },
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={t('studyoPrompt.selectPrompt')}
                            placeholder={t('studyoPrompt.selectPromptPlaceholder')}
                            fullWidth
                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                },
                            }}
                        />
                    )}
                />
            )}

            {/* Prompt Metni - TextField + AI Enhance Button */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                    fullWidth
                    required={required}
                    label={label || t('studyoPrompt.editPromptText')}
                    placeholder={placeholder || t('mannequin.aiGeneration.configuration.promptPlaceholder')}
                    value={promptText}
                    onChange={(e) => handleTextChange(e.target.value)}
                    error={error}
                    helperText={helperText}
                    multiline
                    minRows={4}
                    maxRows={20}
                    sx={{
                        '& .MuiInputBase-root': {
                            alignItems: 'flex-start',
                        },
                        '& textarea': {
                            resize: 'vertical',
                        },
                    }}
                />
                <Tooltip title={t('studyoPrompt.enhanceWithAI')}>
                    <span>
                        <IconButton
                            onClick={() => setEnhanceModalOpen(true)}
                            disabled={!promptText?.trim()}
                            color="primary"
                            sx={{
                                mt: 1,
                                border: '1px solid',
                                borderColor: 'primary.main',
                                borderRadius: 1,
                                '&:hover': { backgroundColor: 'primary.light', color: 'white' },
                                '&.Mui-disabled': { borderColor: 'action.disabled' },
                            }}
                        >
                            <AutoAwesomeIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {/* Prompt Enhance Modal */}
            <PromptEnhanceModal
                open={enhanceModalOpen}
                onClose={() => setEnhanceModalOpen(false)}
                originalPrompt={promptText}
                onApply={(enhanced) => onPromptTextChange(enhanced)}
            />
        </Box>
    );
};
